'use client';

import { EPlatform } from '@/utils/enums/platform-enum';
import { Box } from '@mui/material';
import Image from '@/components/Image';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

// 类型定义
interface BenefitItem {
  title: string;
  description: string;
  image: string;
}

interface PageData {
  title1: string;
  title2: string;
  bg_image: string;
  benefit_list: BenefitItem[];
}

interface Props {
  pageData: PageData;
  sDPageExp: (
    mkt_name: string,
    pits_index: number,
    pits_jump_type?: string,
    pits_jump_content?: string,
  ) => void;
  deviceType: EPlatform;
}

export default function BenefitArr({ pageData, sDPageExp, deviceType }: Props) {
  const [benefitRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // 曝光埋点
  useEffect(() => {
    if (inView) {
      sDPageExp('大使权益', 0);
    }
  }, [inView, sDPageExp]);

  // 安全检查：确保benefit_list数组存在
  if (!pageData?.benefit_list || !Array.isArray(pageData.benefit_list)) {
    return null;
  }

  return (
    <Box
      ref={benefitRef}
      className="w-full px-[14px] py-[40px] lg:px-[60px] lg:py-[80px] 2xl:px-[96px] 2xl:py-[144px]"
      sx={{
        backgroundImage: `url(${pageData.bg_image})`,
        backgroundSize:
          deviceType !== EPlatform.WEB
            ? 'auto 124px'
            : {
                sm: '600px auto',
                xl: '700px auto',
                '2xl': '900px auto',
              },
        backgroundPosition:
          deviceType !== EPlatform.WEB ? 'calc(100% + 30px) 46px' : '-200px calc(100% + 80px)',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Box className="lg:justify-between lg:flex lg:flex-start 2xl:justify-between 2xl:flex 2xl:flex-start">
        <Box className="text-center px-[10px] lg:px-[0px] lg:w-[400px] lg:translate-y-[-20px] 2xl:px-[0px] 2xl:w-[531px] 2xl:translate-y-[-27px] lg:mr-[40px]">
          <p
            className="font-bb text-left text-[55px] leading-[66px] lg:text-[120px] lg:leading-[144px] 2xl:text-[160px] 2xl:leading-[192px] text-primary-text"
            dangerouslySetInnerHTML={{ __html: pageData.title1 }}
          />
          <p
            className="font-bb text-right text-[35px] leading-[42px] translate-y-[-12px] lg:text-[80px] lg:leading-[72px] lg:translate-y-[-12px] lg:text-left 2xl:text-[100px] 2xl:leading-[90px] 2xl:translate-y-[-15px] 2xl:text-left text-primary-text"
            dangerouslySetInnerHTML={{ __html: pageData.title2 }}
          />
        </Box>

        <Box className="lg:grid lg:grid-cols-3 lg:gap-y-[30px] mt-[70px] lg:mt-[0px] 2xl:grid 2xl:grid-cols-3 2xl:gap-y-[44px] 2xl:mt-[0px]">
          {pageData.benefit_list.map((benefit: BenefitItem, index: number) => (
            <Box
              key={`benefit-${benefit.title || index}`}
              className="pl-[10px] mb-[24px] flex lg:mb-[0px] lg:px-[16px] lg:py-[16px] lg:box-border lg:bg-primary-white lg:rounded-[12px] lg:border lg:border-[#ffffff] lg:block lg:cursor-pointer lg:max-w-[345px] 2xl:mb-[0px] 2xl:px-[20px] 2xl:py-[20px] 2xl:box-border 2xl:bg-primary-white 2xl:rounded-[16px] 2xl:border 2xl:border-[#ffffff] 2xl:block 2xl:cursor-pointer"
              sx={{
                '@media (min-width: 1024px)': {
                  '&:hover': {
                    boxShadow: '0px 1px 8px 0px rgba(0,0,0,0.08)',
                    border: '1px solid #EEEEEE',
                  },
                },
                '@media (min-width: 1536px)': {
                  '&:hover': {
                    boxShadow: '0px 1px 12px 0px rgba(0,0,0,0.09)',
                    border: '1px solid #EEEEEE',
                  },
                },
              }}
            >
              <Image
                src={benefit.image}
                alt={benefit.title}
                width={deviceType !== EPlatform.WEB ? 50 : 80}
                height={deviceType !== EPlatform.WEB ? 50 : 80}
                className="w-[50px] h-[50px] mr-[16px] lg:w-[60px] lg:h-[60px] lg:mb-[16px] 2xl:w-[80px] 2xl:h-[80px] 2xl:mb-[24px]"
              />
              <Box>
                <h4
                  className="font-bb text-[14px] leading-[17px] mb-[4px] lg:text-[16px] lg:leading-[20px] lg:mb-[12px] 2xl:text-[20px] 2xl:leading-[24px] 2xl:mb-[16px] text-primary-text"
                  dangerouslySetInnerHTML={{ __html: benefit.title }}
                />
                <p
                  className="font-br text-[12px] leading-[18px] lg:text-[14px] lg:leading-[20px] 2xl:text-[16px] 2xl:leading-[24px] text-primary-text"
                  dangerouslySetInnerHTML={{ __html: benefit.description }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
